import React, { useEffect, useId, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type { ScoreResultPayload } from './scoreResultUrl';
import { mailtoAuditFallback, submitLead } from '../lib/leads';
import { trackAuditRequest } from '../lib/analytics';
const DRAFT_KEY = 'authorLeverage:auditDraft';
type AuditDraft = {
  name: string;
  email: string;
  note: string;
};
function readDraft(): AuditDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuditDraft>;
    return {
      name: typeof parsed.name === 'string' ? parsed.name : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
      note: typeof parsed.note === 'string' ? parsed.note : ''
    };
  } catch {
    return null;
  }
}
function writeDraft(draft: AuditDraft) {
  try {
    if (!draft.name.trim() && !draft.email.trim() && !draft.note.trim()) {
      window.sessionStorage.removeItem(DRAFT_KEY);
      return;
    }
    window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Draft is best-effort if storage is blocked.
  }
}
function clearDraft() {
  try {
    window.sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore
  }
}
type AuditRequestFormProps = {
  score?: ScoreResultPayload | null;
  helperText: string;
};
export function AuditRequestForm({
  score = null,
  helperText
}: AuditRequestFormProps) {
  const [name, setName] = useState(() => readDraft()?.name ?? '');
  const [email, setEmail] = useState(() => readDraft()?.email ?? '');
  const [note, setNote] = useState(() => readDraft()?.note ?? '');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'mailto' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    email?: string;
  }>({});
  const [restoredDraft, setRestoredDraft] = useState(() => {
    const draft = readDraft();
    return Boolean(draft && (draft.name || draft.email || draft.note));
  });
  const submittingRef = useRef(false);
  const errorId = useId();
  const nameErrorId = useId();
  const emailErrorId = useId();
  const hasScore = Boolean(score);
  const isDirty = status !== 'success' && Boolean(name.trim() || email.trim() || note.trim());
  useEffect(() => {
    if (status === 'success') return;
    writeDraft({
      name,
      email,
      note
    });
  }, [name, email, note, status]);
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);
  const mailtoContext = () => ({
    name: name.trim() || undefined,
    email: email.trim() || undefined,
    note: note.trim() || undefined,
    pageUrl: typeof window !== 'undefined' ? window.location.href : undefined
  });
  const openMailtoOnce = () => {
    const href = mailtoAuditFallback(score, mailtoContext());
    // Assign once — avoids duplicate windows from rapid clicks / popup + assign.
    window.location.assign(href);
  };
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current || status === 'submitting' || status === 'success') {
      return;
    }
    const nextFieldErrors: {
      name?: string;
      email?: string;
    } = {};
    if (!name.trim()) nextFieldErrors.name = 'Enter your name.';
    if (!email.trim()) nextFieldErrors.email = 'Enter your email address.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      nextFieldErrors.email = 'Enter a valid email address.';
    }
    if (nextFieldErrors.name || nextFieldErrors.email) {
      setFieldErrors(nextFieldErrors);
      setError('Please fix the highlighted fields.');
      setStatus('error');
      return;
    }
    setFieldErrors({});
    submittingRef.current = true;
    setStatus('submitting');
    setError(null);
    const result = await submitLead({
      name: name.trim(),
      email: email.trim(),
      note: note.trim() || undefined,
      source: 'audit_request',
      score
    });
    if (result.ok) {
      trackAuditRequest('form');
      clearDraft();
      setRestoredDraft(false);
      setStatus('success');
      submittingRef.current = false;
      return;
    }

    // Fall back to mailto when plumbing is missing or failed.
    trackAuditRequest('mailto_fallback');
    setStatus('mailto');
    setError(result.mode === 'error' ? result.error || 'Could not save your request online. Your email app will open instead.' : 'Your email app will open with your score details so you can send the request.');
    openMailtoOnce();
    // Keep mailto state so a second click does not spawn another compose window.
    submittingRef.current = false;
  };
  if (status === 'success') {
    return <div className="rounded-3xl bg-navy px-7 py-10 text-center text-paper md:px-12" data-id="element-36" data-mp="xlht3m7l6btz">
        <h2 className="font-serif text-3xl leading-tight md:text-4xl" data-id="element-37" data-mp="11m1mqfsyaxbd">
          Request received.
        </h2>
        <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-paper/75" data-id="element-38" data-mp="16j0h8zgj16a3">
          Thanks{name ? `, ${name.split(' ')[0]}` : ''}. We’ll follow up about a
          Reader Path Audit
          {hasScore ? ' using the score you just completed' : ''}.
        </p>
      </div>;
  }
  const fieldClass = 'w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-paper placeholder:text-paper/70 focus:outline-none focus:ring-2 focus:ring-seaglass';
  return <section className="mt-10 rounded-3xl bg-navy px-7 py-10 text-center text-paper md:px-12" aria-labelledby="audit-heading" data-id="element-39" data-mp="1ua5wpdik1wjv">
      <h2 id="audit-heading" className="font-serif text-3xl leading-tight md:text-4xl" data-id="element-40" data-mp="3r3qwg6uvalts">
        See what a clearer path could unlock.
      </h2>
      <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-paper/75" data-id="element-41" data-mp="136sh6yo7qu77">
        {helperText}
      </p>

      {restoredDraft && <p className="mx-auto mt-4 max-w-md text-sm text-seaglass-light" role="status" data-id="element-42" data-mp="y8hc118v57vb">
          Your previous draft was restored. Leaving or refreshing this page will
          keep it until you send the request.
        </p>}

      <form onSubmit={onSubmit} noValidate className="mx-auto mt-8 max-w-md space-y-3 text-left" data-id="element-43" data-mp="176uc8ozg0pdi">
        <label className="block" data-id="element-44" data-mp="1m43i0nphtvor">
          <span className="mb-1.5 block text-sm font-medium text-paper/90" data-id="element-45" data-mp="1m3bp5kna6c26">
            Name <span className="text-sand" data-id="element-46" data-mp="1h4h5yhleilhj">(required)</span>
          </span>
          <input type="text" name="name" autoComplete="name" required aria-required="true" aria-invalid={fieldErrors.name ? true : undefined} aria-describedby={fieldErrors.name ? nameErrorId : undefined} placeholder="Your name" value={name} onChange={e => {
          setName(e.target.value);
          setRestoredDraft(false);
          if (fieldErrors.name) setFieldErrors(f => ({
            ...f,
            name: undefined
          }));
        }} className={fieldClass} data-id="element-47" data-mp="1uwop36jridxr" />
          {fieldErrors.name && <p id={nameErrorId} className="mt-1.5 text-sm text-sand" role="alert" data-id="element-48" data-mp="2ogmur87z7zfx">
              {fieldErrors.name}
            </p>}
        </label>
        <label className="block" data-id="element-49" data-mp="2ejrt4ggepixf">
          <span className="mb-1.5 block text-sm font-medium text-paper/90" data-id="element-50" data-mp="2eiqiue77t0gr">
            Email <span className="text-sand" data-id="element-51" data-mp="20rtqmoh7ala8">(required)</span>
          </span>
          <input type="email" name="email" autoComplete="email" required aria-required="true" aria-invalid={fieldErrors.email ? true : undefined} aria-describedby={fieldErrors.email ? emailErrorId : undefined} placeholder="you@example.com" value={email} onChange={e => {
          setEmail(e.target.value);
          setRestoredDraft(false);
          if (fieldErrors.email) setFieldErrors(f => ({
            ...f,
            email: undefined
          }));
        }} className={fieldClass} data-id="element-52" data-mp="20qirx32m43pz" />
          {fieldErrors.email && <p id={emailErrorId} className="mt-1.5 text-sm text-sand" role="alert" data-id="element-53" data-mp="22abw7ylu9ly1">
              {fieldErrors.email}
            </p>}
        </label>
        <label className="block" data-id="element-54" data-mp="1xc1zvkodofau">
          <span className="mb-1.5 block text-sm font-medium text-paper/90" data-id="element-55" data-mp="1scfo7fo77745">
            Optional note
          </span>
          <textarea name="note" rows={3} placeholder="Timeline, book title, etc." value={note} onChange={e => {
          setNote(e.target.value);
          setRestoredDraft(false);
        }} className={`resize-y ${fieldClass}`} data-id="element-56" data-mp="1ndul98hka9c0" />
        </label>

        {error && <p id={errorId} className="text-sm text-sand" role="alert" data-id="element-57" data-mp="1eiy49trlnsdl">
            {error}
          </p>}

        <button type="submit" disabled={status === 'submitting' || status === 'mailto'} className="inline-flex min-h-[56px] w-full items-center justify-center rounded-xl bg-sand px-8 py-4 font-bold text-navy transition-colors hover:bg-sand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy disabled:cursor-not-allowed disabled:opacity-60" data-id="element-58" data-mp="19jty7bzz2arh">
          {status === 'submitting' ? 'Sending…' : status === 'mailto' ? 'Email app opened' : 'Request a Reader Path Audit'}
          <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" data-id="element-59" data-mp="3g9x7o40afmny" />
        </button>
      </form>

      <p className="mt-4 text-sm text-paper/70" data-id="element-60" data-mp="3l9t9bwlij20c">
        We’ll follow up by email
        {hasScore ? '. Your score details are included automatically.' : '.'}
      </p>

      {status === 'mailto' && <p className="mt-3 text-sm text-paper/70" data-id="element-61" data-mp="2sk2xju7csc7e">
          If your email app did not open, use Prefer email below, or write
          john@growththatholds.com.
        </p>}

      <a href={mailtoAuditFallback(score, mailtoContext())} onClick={() => trackAuditRequest('mailto_fallback')} className="mt-3 inline-block text-sm font-medium text-seaglass-light underline underline-offset-4 hover:text-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-62" data-mp="32afe7bc1rl89">
        Prefer email instead
      </a>
    </section>;
}